import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";

type EmailProvider = "smtp" | "graph";

interface EmailSettings {
  provider: EmailProvider;
  from_address: string | null;
  from_name: string | null;
  booking_from_address: string | null;
  booking_from_name: string | null;
  reply_to: string | null;
  smtp_host: string | null;
  smtp_port: number;
  smtp_user: string | null;
  smtp_password_configured: boolean;
  smtp_use_tls: boolean;
}

interface TenantSettings {
  email: EmailSettings;
}

interface EmailIntegrationPanelProps {
  canWrite: boolean;
}

export function EmailIntegrationPanel({ canWrite }: EmailIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [emailProvider, setEmailProvider] = useState<EmailProvider>("smtp");
  const [emailFrom, setEmailFrom] = useState("");
  const [emailFromName, setEmailFromName] = useState("");
  const [bookingEmailFrom, setBookingEmailFrom] = useState("");
  const [bookingEmailFromName, setBookingEmailFromName] = useState("");
  const [emailReplyTo, setEmailReplyTo] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [smtpPasswordConfigured, setSmtpPasswordConfigured] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        setEmailProvider(settings.email.provider);
        setEmailFrom(settings.email.from_address ?? "");
        setEmailFromName(settings.email.from_name ?? "");
        setBookingEmailFrom(settings.email.booking_from_address ?? "");
        setBookingEmailFromName(settings.email.booking_from_name ?? "");
        setEmailReplyTo(settings.email.reply_to ?? "");
        setSmtpHost(settings.email.smtp_host ?? "");
        setSmtpPort(String(settings.email.smtp_port));
        setSmtpUser(settings.email.smtp_user ?? "");
        setSmtpPassword("");
        setSmtpUseTls(settings.email.smtp_use_tls);
        setSmtpPasswordConfigured(settings.email.smtp_password_configured);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body: Record<string, unknown> = {
        provider: emailProvider,
        from_address: emailFrom.trim() || null,
        from_name: emailFromName.trim() || null,
        booking_from_address: bookingEmailFrom.trim() || null,
        booking_from_name: bookingEmailFromName.trim() || null,
        reply_to: emailReplyTo.trim() || null,
        smtp_host: smtpHost.trim() || null,
        smtp_port: Number.parseInt(smtpPort.trim() || "587", 10),
        smtp_user: smtpUser.trim() || null,
        smtp_use_tls: smtpUseTls,
      };
      if (smtpPassword.trim()) {
        body.smtp_password = smtpPassword.trim();
      } else if (!smtpPasswordConfigured) {
        body.smtp_password = "";
      }
      await api.patch<TenantSettings>("/api/v1/settings/email", body);
      setSuccess(true);
      setSmtpPassword("");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "E-Mail-Einstellungen konnten nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
          <fieldset className="space-y-3" disabled={!canWrite}>
            <label className="flex max-w-md flex-col gap-1 text-sm">
              Versandweg
              <select
                className="eh-select-input"
                value={emailProvider}
                onChange={(e) => setEmailProvider(e.target.value as EmailProvider)}
              >
                <option value="smtp">SMTP</option>
                <option value="graph">Microsoft Graph (angemeldeter Benutzer)</option>
              </select>
            </label>
            <label className="flex max-w-md flex-col gap-1 text-sm">
              Absenderadresse
              <input
                type="email"
                className="eh-input"
                value={emailFrom}
                onChange={(e) => setEmailFrom(e.target.value)}
                placeholder="office@campingplatz.de"
              />
            </label>
            <label className="flex max-w-md flex-col gap-1 text-sm">
              Absendername (optional)
              <input
                className="eh-input"
                value={emailFromName}
                onChange={(e) => setEmailFromName(e.target.value)}
              />
            </label>
            <div className="space-y-3 rounded-lg border border-separator p-3">
              <p className="text-sm font-medium text-label">Buchungs-E-Mails</p>
              <p className="text-xs text-label-secondary">
                Buchungsbestätigung, Gästeportal-Anmeldecode und weitere automatische
                Buchungsbenachrichtigungen. Leer lassen, um die allgemeine Absenderadresse zu
                verwenden.
              </p>
              <label className="flex max-w-md flex-col gap-1 text-sm">
                Absenderadresse Buchung
                <input
                  type="email"
                  className="eh-input"
                  value={bookingEmailFrom}
                  onChange={(e) => setBookingEmailFrom(e.target.value)}
                  placeholder="booking@campingplatz-mainkur.de"
                />
              </label>
              <label className="flex max-w-md flex-col gap-1 text-sm">
                Absendername Buchung (optional)
                <input
                  className="eh-input"
                  value={bookingEmailFromName}
                  onChange={(e) => setBookingEmailFromName(e.target.value)}
                  placeholder="Campingplatz Mainkur – Buchung"
                />
              </label>
            </div>
            <label className="flex max-w-md flex-col gap-1 text-sm">
              Antwort an (optional)
              <input
                type="email"
                className="eh-input"
                value={emailReplyTo}
                onChange={(e) => setEmailReplyTo(e.target.value)}
              />
            </label>
            {emailProvider === "smtp" ? (
              <>
                <label className="flex max-w-md flex-col gap-1 text-sm">
                  SMTP-Server
                  <input
                    className="eh-input"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.example.com"
                  />
                </label>
                <label className="flex max-w-xs flex-col gap-1 text-sm">
                  SMTP-Port
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    className="eh-input"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                  />
                </label>
                <label className="flex max-w-md flex-col gap-1 text-sm">
                  SMTP-Benutzer (optional)
                  <input
                    className="eh-input"
                    value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)}
                  />
                </label>
                <label className="flex max-w-md flex-col gap-1 text-sm">
                  SMTP-Passwort
                  {smtpPasswordConfigured ? (
                    <span className="text-xs text-label-tertiary">
                      Gespeichert – leer lassen, um beizubehalten
                    </span>
                  ) : null}
                  <input
                    type="password"
                    className="eh-input"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-separator"
                    checked={smtpUseTls}
                    onChange={(e) => setSmtpUseTls(e.target.checked)}
                  />
                  <span className="text-label">STARTTLS verwenden</span>
                </label>
              </>
            ) : (
              <p className="text-sm text-label-secondary">
                E-Mails werden über das Konto des angemeldeten Microsoft-Benutzers versendet
                (Mail.Send). Absenderadresse dient als Anzeigename in Vorlagen.
              </p>
            )}
          </fieldset>
          {error ? <p className="text-sm text-status-red">{error}</p> : null}
          {success ? (
            <p className="text-sm text-status-green">E-Mail-Einstellungen gespeichert.</p>
          ) : null}
      {canWrite ? (
        <div className="flex justify-end border-t border-separator pt-4">
          <Button type="submit" loading={saving}>
            E-Mail-Einstellungen speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
